import React, { useEffect, useState } from "react";
import { Plus, X, Trash2, Edit2, Save, Eye, EyeOff, Zap } from "lucide-react";
import { api } from "../../lib/api";

const blank = {
  label_en: "",
  label_pt: "",
  url: "",
  sort_order: 0,
  visible: true,
  accent: false,
  external: false,
};

export const AdminNavTab = () => {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/nav-items");
    setItems(data || []);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const reset = () => {
    setForm(blank);
    setEditingId(null);
    setShowCreate(false);
    setError("");
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      label_en: it.label_en || "",
      label_pt: it.label_pt || "",
      url: it.url || "",
      sort_order: it.sort_order ?? 0,
      visible: it.visible !== false,
      accent: !!it.accent,
      external: !!it.external,
    });
    setShowCreate(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        label_en: form.label_en,
        label_pt: form.label_pt || form.label_en,
        url: form.url,
        sort_order: Number(form.sort_order) || 0,
        visible: !!form.visible,
        accent: !!form.accent,
        external: !!form.external,
      };
      if (editingId) {
        await api.patch(`/admin/nav-items/${editingId}`, payload);
      } else {
        await api.post("/admin/nav-items", payload);
      }
      await load();
      reset();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVisible = async (it) => {
    try {
      await api.patch(`/admin/nav-items/${it.id}`, { visible: !it.visible });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.label_en}" from navigation?`)) return;
    try {
      await api.delete(`/admin/nav-items/${it.id}`);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section data-testid="admin-nav-section">
      <div className="flex items-center justify-between mb-6">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ TOP NAVIGATION — CUSTOM ITEMS</p>
        <button
          onClick={() => (showCreate ? reset() : setShowCreate(true))}
          className="bg-kult-pink text-black font-display text-base px-4 py-2 hover:bg-kult-yellow inline-flex items-center gap-2"
          data-testid="nav-toggle-create"
        >
          {showCreate ? <X size={16} /> : <Plus size={16} />} {showCreate ? "CLOSE" : "NEW NAV ITEM"}
        </button>
      </div>

      <p className="text-kult-muted text-sm mb-2">
        Add extra links to the top bar (e.g. <em>Press</em>, <em>Lookbook</em>, <em>Discord</em>). These appear after the built-in items.
      </p>
      <p className="text-kult-muted text-xs mb-6">
        To hide/relabel the built-in items (SHOP, TICKETS, EVENTS, ROSTER, ABOUT), use <strong>SITE COPY → NAVIGATION</strong>.
      </p>

      {showCreate && (
        <form
          onSubmit={submit}
          className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="admin-nav-form"
        >
          <h3 className="md:col-span-2 font-display text-2xl">
            {editingId ? "EDIT NAV ITEM" : "NEW NAV ITEM"}
          </h3>
          <Field
            label="LABEL (EN)"
            required
            value={form.label_en}
            onChange={(e) => setForm({ ...form, label_en: e.target.value })}
            testId="nav-label-en"
          />
          <Field
            label="LABEL (PT)"
            value={form.label_pt}
            onChange={(e) => setForm({ ...form, label_pt: e.target.value })}
            testId="nav-label-pt"
          />
          <Field
            label="URL (internal e.g. /press OR full https://...)"
            required
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            testId="nav-url"
            className="md:col-span-2"
          />
          <Field
            label="SORT ORDER (lower = first)"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
            testId="nav-sort"
          />
          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 text-sm font-mono text-white cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.visible}
                onChange={(e) => setForm({ ...form, visible: e.target.checked })}
                data-testid="nav-visible"
                className="w-4 h-4 accent-kult-pink"
              />
              <span className="tracking-[0.2em] text-xs">VISIBLE</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm font-mono text-white cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.accent}
                onChange={(e) => setForm({ ...form, accent: e.target.checked })}
                data-testid="nav-accent"
                className="w-4 h-4 accent-kult-yellow"
              />
              <span className="tracking-[0.2em] text-xs">ACCENT (yellow ⚡ — like TICKETS)</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm font-mono text-white cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.external}
                onChange={(e) => setForm({ ...form, external: e.target.checked })}
                data-testid="nav-external"
                className="w-4 h-4 accent-kult-pink"
              />
              <span className="tracking-[0.2em] text-xs">OPEN IN NEW TAB</span>
            </label>
          </div>
          {error && <p className="md:col-span-2 font-mono text-xs text-kult-pink">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="md:col-span-2 bg-kult-pink text-black font-display text-xl py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="nav-submit"
          >
            <Save size={18} /> {submitting ? "SAVING..." : editingId ? "SAVE" : "CREATE"}
          </button>
        </form>
      )}

      <div className="border border-kult-border" data-testid="nav-items-list">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-kult-border bg-kult-black2 font-mono text-[10px] tracking-[0.3em] text-kult-muted">
          <span className="col-span-1">#</span>
          <span className="col-span-2">LABEL (EN)</span>
          <span className="col-span-2">LABEL (PT)</span>
          <span className="col-span-3">URL</span>
          <span className="col-span-2">FLAGS</span>
          <span className="col-span-2 text-right">ACTIONS</span>
        </div>
        {items.length === 0 && (
          <p className="px-4 py-6 font-mono text-xs text-kult-muted">No custom nav items yet.</p>
        )}
        {items.map((it) => (
          <div
            key={it.id}
            className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-kult-border items-center"
            data-testid={`nav-row-${it.id}`}
          >
            <span className="col-span-1 font-mono text-xs text-kult-muted">{it.sort_order ?? 0}</span>
            <span className="col-span-2 font-display text-base">{it.label_en}</span>
            <span className="col-span-2 font-mono text-xs text-kult-muted">{it.label_pt}</span>
            <span className="col-span-3 font-mono text-[11px] text-white break-all">{it.url}</span>
            <span className="col-span-2 inline-flex items-center gap-2 text-[10px] text-kult-muted">
              {it.accent && <span title="Accent" className="text-kult-yellow"><Zap size={12} /></span>}
              {it.external && <span title="External">↗</span>}
              <button onClick={() => toggleVisible(it)} title="Toggle visibility" data-testid={`nav-toggle-${it.id}`}>
                {it.visible === false ? <EyeOff size={14} className="text-kult-muted" /> : <Eye size={14} className="text-kult-yellow" />}
              </button>
            </span>
            <div className="col-span-2 flex items-center justify-end gap-2">
              <button
                onClick={() => startEdit(it)}
                className="p-1.5 border border-kult-border hover:border-kult-pink"
                data-testid={`nav-edit-${it.id}`}
                title="Edit"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={() => remove(it)}
                className="p-1.5 border border-kult-border hover:border-kult-pink text-kult-pink"
                data-testid={`nav-delete-${it.id}`}
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

function Field({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{label}</span>
      <input
        {...props}
        data-testid={testId}
        className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-sm focus:border-kult-pink outline-none"
      />
    </label>
  );
}
